/**
 * Each list's **New ▾** — `docs/new-request.md` §13.1, Board 1 A–C, G.
 *
 * The green half does exactly what the page's button did (New contact, New
 * property, New job — Aaron, comment 1: "they'll all default to creating a new
 * record on their associated list"); the chevron's menu holds that and
 * **New request**.
 *
 * **The list engine is not changed** (do-not-substitute 0). This is the
 * page's own `actions` node and nothing more, so `ListTools` renders it as
 * given: two buttons side by side, which its `[&_button]` rules keep at 36px.
 * The menu is portalled to `document.body` for the same reason — anything
 * inside ListTools' wrapper picks up those rules.
 *
 * On a phone it is **one** button with the + glyph, which ListTools' squash
 * makes the drawn 36px green square, opening a two-row bottom sheet (Board 1 G).
 */
import { useRef, useState, type ComponentType } from 'react'
import { createPortal } from 'react-dom'
import { Button, buttonClass } from '../components/primitives.js'
import { Popover } from '../components/overlays.js'
import { IconPlus, type IconProps } from '../frame/icons.js'
import { usePhone } from '../lib/use-narrow.js'
import { cn } from '../lib/cn.js'
import { PlusGlyph } from '../components/keycap.js'
import { BottomMenu } from './bottom-menu.js'
import { MenuRow, NEW_REQUEST_KEYS, NEW_REQUEST_SUB, SheetRow } from './create-rows.js'

export interface NewSplitButtonProps {
  /** *New contact* · *New property* · *New job*. */
  label: string
  /** The glyph of the page's own record, for its menu row. */
  icon: ComponentType<IconProps>
  /** What the record's row says under it: *just the contact — the full form*. */
  sub: string
  /** The phone sheet's shorter sub-line: *just the contact*. */
  phoneSub: string
  /** The page's own New — the green half. */
  onNew?: () => void
  /** New request. Absent: the person cannot take one, and the plain button stays. */
  onNewRequest?: () => void
}

export function NewSplitButton({
  label, icon: Icon, sub, phoneSub, onNew, onNewRequest,
}: NewSplitButtonProps) {
  const phone = usePhone()
  const chevron = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)

  /* §19 B19: no own New, a New request: the plain green New request button. */
  if (!onNew && onNewRequest) {
    return (
      <Button variant="primary" onClick={onNewRequest}>
        <IconPlus size={15} />New request
      </Button>
    )
  }
  /* Without `jobs ≥ edit` the list's button is today's (§17). */
  if (!onNewRequest) {
    return onNew ? <Button variant="primary" onClick={onNew}><IconPlus size={15} />{label}</Button> : null
  }

  const choose = (run: () => void) => { setOpen(false); run() }

  if (phone) {
    return (
      <>
        {/* While its sheet is up, the + is pressed: accent-dark in a 3px ring (Board 1 G). */}
        <Button variant="primary" aria-label="New" onClick={() => setOpen(true)}
          className={cn(open && 'border-accent-dark bg-accent-dark ring-[3px] ring-accent-ring')}>
          <IconPlus size={15} />
        </Button>
        <BottomMenu open={open} onClose={() => setOpen(false)} label="New">
          <SheetRow first icon={<Icon size={17} />} title={label} sub={phoneSub} tone="info"
            onClick={() => choose(onNew!)} />
          <SheetRow icon={<PlusGlyph size={16} stroke={2.2} />} title="New request"
            sub={NEW_REQUEST_SUB} tone="accent" onClick={() => choose(onNewRequest)} />
        </BottomMenu>
      </>
    )
  }

  return (
    /*
     * One flex item for ListTools' row, so its gap does not come between the
     * two halves; its `[&_button]` rules still reach both and keep them 36px.
     */
    <span className="inline-flex items-stretch">
      {/* Board 1 B: 36px, 0 13px, 12.5/600, a 14px + — the toolbar size, square on the right. */}
      <Button variant="primary" size="toolbar" onClick={onNew} className="rounded-r-none px-[13px]">
        <PlusGlyph size={14} stroke={2.2} />{label}
      </Button>
      <button
        type="button"
        /* Not naming the page's own New: a search for that button must find one. */
        aria-label="More ways to start"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={buttonClass({
          variant: 'primary',
          size: 'toolbar',
          /* Open, the fill goes accent-dark to the edge (Board 1 A–C). */
          className: cn('relative w-8 rounded-l-none px-0', open && 'border-accent-dark bg-accent-dark'),
        })}
      >
        {/*
         * The hairline between the halves: white at 35%, laid exactly over
         * this half's left border, so it reads over whichever green is there.
         */}
        <span aria-hidden className="absolute -inset-y-px -left-px w-px bg-white/35" />
        {/*
         * What the menu hangs from: a line of no height 12px under the
         * button, so with the popover's own 6px gap the menu opens 18px
         * below, as drawn — and nothing there to press.
         */}
        <span ref={chevron} aria-hidden className="absolute inset-x-0 top-[calc(100%+12px)] h-0" />
        <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden className="shrink-0" fill="none"
          stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && createPortal(
        <Popover
          open
          onClose={() => setOpen(false)}
          anchor={chevron.current}
          align="right"
          width={340}
          label="New"
          className="rounded-card py-1"
        >
          <div role="menu" aria-label="New" className="flex flex-col">
            <MenuRow icon={<Icon size={17} />} title={label} sub={sub} onClick={() => choose(onNew!)} />
            <div className="my-1 h-px bg-line" />
            <MenuRow feature title="New request" sub={NEW_REQUEST_SUB} keys={NEW_REQUEST_KEYS}
              onClick={() => choose(onNewRequest)} />
          </div>
        </Popover>,
        document.body,
      )}
    </span>
  )
}
